import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction
} from 'react';
import {
  AddOn,
  Appointment,
  BookingAvailabilitySlot,
  Company,
  MedicalCertificate,
  Service,
  SystemConfig,
  Venue
} from '@/types';
import { api } from '@/services/api';
import { normalizeDateOnly } from '@/lib/dates';
import { filterPastAvailabilitySlots, getAvailableTimeSlotsForDate } from '@/lib/scheduling';
import { slotTimesForSelection } from '@/lib/bookingSlots';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import {
  buildCertificateFileName,
  buildCertificateHtml,
  openCertificatePrintWindow,
  resolveCertificateClientName
} from '@/lib/certificateRenderer';
import { downloadCertificatePdf } from '@/lib/certificatePdf';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';

interface DashboardParams {
  company: Company;
  servicesById: Map<string, Service>;
  venuesById: Map<string, Venue>;
  addOnsById: Map<string, AddOn>;
  certificatesById: Map<string, MedicalCertificate>;
  systemConfig?: SystemConfig;
  onForceRefresh?: () => Promise<void>;
}

interface UseCompanyDashboardBookingsState {
  editingBooking: Appointment | null;
  editingBookingOriginal: Appointment | null;
  setEditingBooking: Dispatch<SetStateAction<Appointment | null>>;
  isLoadingEditingAvailability: boolean;
  editingTimeOptions: string[];
  bookingActionError: string;
  isSavingBooking: boolean;
  openEditBooking: (app: Appointment) => void;
  closeEditBooking: () => void;
  handleSaveBooking: () => Promise<void>;
  handleCancelBooking: () => Promise<void>;
  handleViewCertificate: (app: Appointment) => void;
  handleDownloadCertificate: (app: Appointment) => Promise<void>;
  downloadingCertificateAppId: string | null;
}

export const useCompanyDashboardBookings = ({
  company,
  servicesById,
  venuesById,
  addOnsById,
  certificatesById,
  systemConfig,
  onForceRefresh
}: DashboardParams): UseCompanyDashboardBookingsState => {
  const [editingBooking, setEditingBooking] = useState<Appointment | null>(null);
  const [editingBookingOriginal, setEditingBookingOriginal] = useState<Appointment | null>(null);
  const [bookingActionError, setBookingActionError] = useState('');
  const [isSavingBooking, setIsSavingBooking] = useState(false);
  const [editingAvailabilitySlots, setEditingAvailabilitySlots] = useState<
    BookingAvailabilitySlot[]
  >([]);
  const [hasEditingAvailabilityData, setHasEditingAvailabilityData] = useState(false);
  const [isLoadingEditingAvailability, setIsLoadingEditingAvailability] = useState(false);
  const [downloadingCertificateAppId, setDownloadingCertificateAppId] = useState<string | null>(
    null
  );

  const refreshCompanyData = useCallback(async () => {
    if (!onForceRefresh) return;
    await onForceRefresh();
  }, [onForceRefresh]);

  const getBookingDurationMinutes = useCallback(
    (booking: Appointment) => {
      const serviceIds =
        Array.isArray(booking.serviceIds) && booking.serviceIds.length > 0
          ? booking.serviceIds
          : [booking.serviceId];
      let duration = serviceIds.reduce(
        (sum, id) => sum + (servicesById.get(id)?.durationMinutes || 0),
        0
      );
      if (duration <= 0) duration = 30;

      for (const addOnId of booking.addOnIds || []) {
        const addOn = addOnsById.get(addOnId);
        if (addOn) {
          duration += addOn.durationMinutes || 0;
        }
      }

      return duration;
    },
    [addOnsById, servicesById]
  );

  const openEditBooking = useCallback((app: Appointment) => {
    const snapshot = { ...app, addOnIds: [...(app.addOnIds || [])] };
    setEditingBooking(snapshot);
    setEditingBookingOriginal(snapshot);
    setBookingActionError('');
  }, []);

  const closeEditBooking = useCallback(() => {
    setEditingBooking(null);
    setEditingBookingOriginal(null);
    setBookingActionError('');
    setEditingAvailabilitySlots([]);
    setHasEditingAvailabilityData(false);
    setIsLoadingEditingAvailability(false);
  }, []);

  const handleSaveBooking = useCallback(async () => {
    if (!editingBooking) return;
    setIsSavingBooking(true);
    setBookingActionError('');
    try {
      // customerName is deliberately not sent: the employee on a booking is
      // fixed — the server rejects company changes to identity fields.
      await api.patchBooking(editingBooking.id, {
        customerJobTitle: (editingBooking.customerJobTitle || '').trim(),
        date: editingBooking.date,
        time: editingBooking.time,
        venueId: editingBooking.venueId,
        serviceId: editingBooking.serviceId,
        serviceIds:
          Array.isArray(editingBooking.serviceIds) && editingBooking.serviceIds.length > 0
            ? editingBooking.serviceIds
            : [editingBooking.serviceId],
        addOnIds: editingBooking.addOnIds || []
      });
      closeEditBooking();
      await refreshCompanyData();
      showAlertModal('Appointment updated.');
    } catch (e: unknown) {
      setBookingActionError(normalizeErrorMessage(e, 'Failed to update appointment.'));
    } finally {
      setIsSavingBooking(false);
    }
  }, [closeEditBooking, editingBooking, refreshCompanyData]);

  const handleCancelBooking = useCallback(async () => {
    if (!editingBooking) return;
    const confirmed = await showConfirmModal(
      `Cancel appointment #${editingBooking.id}? This will mark the booking as cancelled.`,
      {
        title: 'Cancel Appointment',
        confirmText: 'Cancel Appointment',
        cancelText: 'Keep Booking',
        isDestructive: true
      }
    );
    if (!confirmed) return;

    setIsSavingBooking(true);
    setBookingActionError('');
    try {
      const baseBooking = editingBookingOriginal || editingBooking;
      await api.patchBooking(baseBooking.id, { status: 'cancelled' });
      closeEditBooking();
      await refreshCompanyData();
      showAlertModal('Appointment cancelled.');
    } catch (e: unknown) {
      setBookingActionError(normalizeErrorMessage(e, 'Failed to cancel appointment.'));
    } finally {
      setIsSavingBooking(false);
    }
  }, [editingBooking, editingBookingOriginal, closeEditBooking, refreshCompanyData]);

  const buildCertificateRender = useCallback(
    (app: Appointment) => {
      if (!app.certificateId || !systemConfig) return null;
      const cert = certificatesById.get(app.certificateId);
      if (!cert) return null;

      return {
        html: buildCertificateHtml({
          template: systemConfig.templates.certificateTemplate.body,
          systemConfig,
          appointment: app,
          certificate: cert,
          clientName: resolveCertificateClientName(app, company.name)
        }),
        fileName: buildCertificateFileName({
          appointment: app,
          certificate: cert
        })
      };
    },
    [certificatesById, company.name, systemConfig]
  );

  const handleViewCertificate = useCallback(
    (app: Appointment) => {
      const render = buildCertificateRender(app);
      if (!render) return;
      openCertificatePrintWindow(render.html, render.fileName);
    },
    [buildCertificateRender]
  );

  const handleDownloadCertificate = useCallback(
    async (app: Appointment) => {
      const render = buildCertificateRender(app);
      if (!render) return;
      setDownloadingCertificateAppId(app.id);
      try {
        const ok = await downloadCertificatePdf(render.html, render.fileName);
        if (!ok) {
          showAlertModal('Could not generate the certificate PDF. Please try again.');
        }
      } finally {
        setDownloadingCertificateAppId(null);
      }
    },
    [buildCertificateRender]
  );

  useEffect(() => {
    if (!editingBooking?.venueId || !editingBooking?.date || !editingBooking?.serviceId) {
      setEditingAvailabilitySlots([]);
      setHasEditingAvailabilityData(false);
      setIsLoadingEditingAvailability(false);
      return;
    }

    const dateValue = normalizeDateOnly(editingBooking.date);
    if (!dateValue) {
      setEditingAvailabilitySlots([]);
      setHasEditingAvailabilityData(false);
      setIsLoadingEditingAvailability(false);
      return;
    }

    let isCurrent = true;
    setIsLoadingEditingAvailability(true);
    setHasEditingAvailabilityData(false);

    api
      .getBookingAvailability({
        venueId: editingBooking.venueId,
        date: dateValue,
        serviceId: editingBooking.serviceId,
        serviceIds:
          Array.isArray(editingBooking.serviceIds) && editingBooking.serviceIds.length > 0
            ? editingBooking.serviceIds
            : [editingBooking.serviceId],
        addOnIds: editingBooking.addOnIds || [],
        excludeAppointmentId: editingBooking.id
      })
      .then((result) => {
        if (!isCurrent) return;
        const slots = filterPastAvailabilitySlots(dateValue, result?.slots || []);
        setEditingAvailabilitySlots(slots);
        setHasEditingAvailabilityData(true);
        setEditingBooking((prev) => {
          if (!prev) return prev;
          // Only re-pick the time when the slot itself moved (date/venue
          // change). A service/add-on-only amendment keeps the booked slot —
          // the server skips schedule/capacity validation for those — so the
          // original time must survive even on past or fully-booked days.
          const slotMoved =
            !editingBookingOriginal ||
            normalizeDateOnly(editingBookingOriginal.date) !== normalizeDateOnly(prev.date) ||
            editingBookingOriginal.venueId !== prev.venueId;
          if (!slotMoved) return prev;
          const stillAvailable = slots.some((slot) => slot.time === prev.time && slot.available);
          if (stillAvailable) return prev;
          const firstAvailable = slots.find((slot) => slot.available)?.time ?? '';
          return { ...prev, time: firstAvailable };
        });
      })
      .catch(() => {
        if (!isCurrent) return;
        setEditingAvailabilitySlots([]);
        setHasEditingAvailabilityData(false);
      })
      .finally(() => {
        if (isCurrent) setIsLoadingEditingAvailability(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [
    editingBooking?.id,
    editingBooking?.date,
    editingBooking?.addOnIds,
    editingBooking?.serviceId,
    editingBooking?.serviceIds,
    editingBooking?.venueId,
    editingBookingOriginal
  ]);

  const editingTimeOptions = useMemo(() => {
    if (!editingBooking) return [];
    const dateValue = normalizeDateOnly(editingBooking.date);
    if (!dateValue) return [];

    const [year, month, day] = dateValue.split('-').map(Number);
    const date = new Date(year, (month || 1) - 1, day || 1);
    if (Number.isNaN(date.getTime())) return [];

    const venue = venuesById.get(editingBooking.venueId);
    const generated = getAvailableTimeSlotsForDate(date, venue?.schedule, {
      requiredDurationMinutes: getBookingDurationMinutes(editingBooking)
    });

    // Shared dropdown slot logic (see @/lib/bookingSlots). Company/customer edit
    // has NO overbook by design — allowOverbook is intentionally omitted.
    return slotTimesForSelection({
      slots: editingAvailabilitySlots,
      hasAvailabilityData: hasEditingAvailabilityData,
      generatedTimes: generated,
      currentTime: editingBooking.time
    });
  }, [
    editingBooking,
    venuesById,
    getBookingDurationMinutes,
    hasEditingAvailabilityData,
    editingAvailabilitySlots
  ]);

  return {
    editingBooking,
    editingBookingOriginal,
    setEditingBooking,
    isLoadingEditingAvailability,
    editingTimeOptions,
    bookingActionError,
    isSavingBooking,
    openEditBooking,
    closeEditBooking,
    handleSaveBooking,
    handleCancelBooking,
    handleViewCertificate,
    handleDownloadCertificate,
    downloadingCertificateAppId
  };
};
